/**
 * The template helpers that used to be global filters.
 *
 * Vue 3 dropped filters outright: `{{ name | cutName(20) }}` is a syntax the compiler no
 * longer understands, and `Vue.filter()` no longer exists - @vue/compat only kept the two
 * alive behind COMPILER_FILTERS. Worse, the halves came apart: filters registered into the
 * app context, which templates read, but vue 2 also merged them into every component's
 * `$options`, which vue 3 does not - so the sites calling one from javascript, through
 * `this.$options.filters`, read undefined and threw.
 *
 * One instance property replaces both halves. `{{ $cutName(name, 20) }}` in a template and
 * `this.$cutName(name, 20)` in javascript are now the same call, and neither depends on a
 * compat flag. They are `app.config.globalProperties`, which vue/createApp.js installs onto
 * every app it builds, the detached popups included. That used to be `Vue.prototype.$x` on
 * the compat global, which wrote into every app at once whether or not it was built here.
 *
 * The plain functions stay importable from the modules below, and js that already has a
 * module to import from should keep doing that - `$`-prefixed properties are for templates
 * and for the components that only ever needed the one call.
 */
import { customTermFilter } from './CustomTerm';
import { activityDate, gbdate, GBDateTime, gbtime } from './dateFilters';
import cutNameFilter from 'shared/filters/name_cutter.filter';
import { abu as addBackUrl, backHrefQuery } from 'shared/utils/back_url_generator';

const cutName = cutNameFilter();

const HELPERS = {
    $customTerm: customTermFilter,
    $cutName: cutName,
    $backHref: addBackUrl,
    $backHrefQuery: backHrefQuery,
    $gbdate: gbdate,
    $GBDateTime: GBDateTime,
    $gbtime: gbtime,
    $activityDate: activityDate,
};

const GlobalHelpersPlugin = {
    install (app) {
        for (const [name, helper] of Object.entries(HELPERS)) {
            app.config.globalProperties[name] = helper;
        }
    }
};

export { GlobalHelpersPlugin };
